"use client";

import Link from "next/link";
import { cn } from "@/lib/utils";
import type { ComponentProps, ReactNode } from "react";

// Shared rainbow fill — set inline (not via a Tailwind class) so it renders
// regardless of class generation. First and last stop match and the tile is
// 200% wide, so `animate-rgb-flow` scrolls background-position seamlessly.
// Applied to both the surface and the blurred glow so they stay in sync.
const rainbowStyle = {
  backgroundImage:
    "linear-gradient(90deg,#ff2d55,#ff8a00,#ffe600,#27ff5b,#00e1ff,#3b6dff,#a855f7,#ff2d55)",
  backgroundSize: "200% auto",
} as const;

interface GradientButtonProps extends Omit<ComponentProps<typeof Link>, "href"> {
  href: string;
  children: ReactNode;
}

/**
 * Supercharged RGB-gradient CTA: a continuously flowing rainbow surface with a
 * matching blurred glow behind it, a shine sweep on hover, and a subtle lift.
 * Motion is disabled under `prefers-reduced-motion` (see `.animate-rgb-flow`).
 */
export default function GradientButton({
  href,
  children,
  className,
  ...props
}: GradientButtonProps) {
  return (
    <Link
      href={href}
      className={cn(
        "group relative inline-flex h-10 items-center justify-center rounded-md",
        "transition-transform duration-200 will-change-transform hover:-translate-y-0.5 active:translate-y-0",
        "outline-none focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        className,
      )}
      {...props}
    >
      {/* Flowing rainbow glow. The element is a rectangle, but a radial mask
          fades it to fully transparent before its edges, so you see a soft
          elliptical aura instead of a boxy rounded-rectangle silhouette. */}
      <span
        aria-hidden
        style={{
          ...rainbowStyle,
          WebkitMaskImage: "radial-gradient(closest-side, #000 25%, transparent 78%)",
          maskImage: "radial-gradient(closest-side, #000 25%, transparent 78%)",
        }}
        className={cn(
          "animate-rgb-flow pointer-events-none absolute -inset-5 opacity-80 blur-md",
          "transition-opacity duration-300 group-hover:opacity-100",
        )}
      />
      {/* Gradient surface */}
      <span
        style={rainbowStyle}
        className={cn(
          "animate-rgb-flow relative inline-flex h-full items-center gap-2 overflow-hidden rounded-md px-5",
          "text-sm font-semibold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.4)]",
          "[&_svg]:size-4 [&_svg]:shrink-0",
        )}
      >
        {/* Diagonal shine that sweeps across on hover */}
        <span
          aria-hidden
          className="pointer-events-none absolute inset-y-0 -left-3/4 w-1/2 -skew-x-[20deg] bg-gradient-to-r from-transparent via-white/55 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-[300%]"
        />
        <span className="relative inline-flex items-center gap-2 [text-shadow:0_1px_2px_rgba(0,0,0,0.4)]">
          {children}
        </span>
      </span>
    </Link>
  );
}
